import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { AppState } from '@/store/store.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import type { FixingSource, FxSwapNds, FxSwapRegular } from '@/store/swap/state.ts';
import type { Possible } from '@/types/common.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { TradeCaptureResponseErrors, TradeCaptureResponseWarnings } from '@/types/trade-capture.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Rate } from '@/utils/rates.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

// ██████╗  █████╗ ██████╗  █████╗ ███╗   ███╗███████╗
// ██╔══██╗██╔══██╗██╔══██╗██╔══██╗████╗ ████║██╔════╝
// ██████╔╝███████║██████╔╝███████║██╔████╔██║███████╗
// ██╔═══╝ ██╔══██║██╔══██╗██╔══██║██║╚██╔╝██║╚════██║
// ██║     ██║  ██║██║  ██║██║  ██║██║ ╚═╝ ██║███████║
// ╚═╝     ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝

export type CheckSwapParams = CheckSwapRegularParams | CheckSwapNdsParams;

export type CheckSwapRegularParams = {
  tileId: string;
  isNonDeliverable: false;
  isNonDeliverableChanged?: boolean;
  properties: ObjectWithUndefinedPropertiesNullable<FxSwapRegular['properties']>;
};

export type CheckSwapNdsParams = {
  tileId: string;
  isNonDeliverable: true;
  isNonDeliverableChanged?: boolean;
  properties: ObjectWithUndefinedPropertiesNullable<FxSwapNds['properties']>;
};

//  █████╗ ██████╗ ██╗
// ██╔══██╗██╔══██╗██║
// ███████║██████╔╝██║
// ██╔══██║██╔═══╝ ██║
// ██║  ██║██║     ██║
// ╚═╝  ╚═╝╚═╝     ╚═╝

export const checkSwapByTradeCapture = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<CheckedSwapResponse, CheckSwapParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getCheckSwapBody(params, state);

      const result = await baseQuery({
        url: '/tradeCaptureBatch/FxSwap',
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      const response = result.data as TradeCaptureSwapResponse;
      const tileResponse = response[params.tileId];

      if (isNotDefined(tileResponse)) {
        return { error: { status: 'CUSTOM_ERROR', error: 'NO_RESPONSE' } };
      }

      return { data: tileResponse };
    },
  });
};

// ██████╗  ██████╗ ██████╗ ██╗   ██╗
// ██╔══██╗██╔═══██╗██╔══██╗╚██╗ ██╔╝
// ██████╔╝██║   ██║██║  ██║ ╚████╔╝
// ██╔══██╗██║   ██║██║  ██║  ╚██╔╝
// ██████╔╝╚██████╔╝██████╔╝   ██║
// ╚═════╝  ╚═════╝ ╚═════╝    ╚═╝

export type CheckSwapBody = {
  sessionId: string;
  tileId: string;
  request: {
    idVersion: number;
    changedFields: {
      currencyPair?: CurrencyPair;
      isNonDeliverable?: boolean;

      legs: {
        0: {
          productName: 'FxSwap';
          currencyPair?: CurrencyPair;

          isUneven?: boolean;
          swapOffMarket?: boolean;

          // update near amount 1 when the first currency is selected,
          // and 2 when the second currency is selected
          // not both at the same time
          nearAmount1String?: string | null;
          nearAmount2String?: string | null;

          nearPaymentDateString?: string | null;

          // update near amount 1 when the first currency is selected,
          // and 2 when the second currency is selected
          // not both at the same time
          farAmount1String?: string | null;
          farAmount2String?: string | null;

          farPaymentDateString?: string | null;

          nearPriceReferenceAsk?: number | null;
          nearPriceReferenceAskString?: string | null;

          nearPriceReferenceBid?: number | null;
          nearPriceReferenceBidString?: string | null;

          farPriceReferenceAsk?: number | null;
          farPriceReferenceAskString?: string | null;

          farPriceReferenceBid?: number | null;
          farPriceReferenceBidString?: string | null;

          xCurrency?: string | null;
          fixingCurrency?: string | null;
          fixingSource?: string | null;
          sndFixingSource?: string | null;
        };
      };
    };
  };
}[];

const getCheckSwapBody = (params: CheckSwapParams, state: AppState): CheckSwapBody => {
  const swap = selectSwapTile(state, params.tileId);

  if (!swap?.tradeCapture) {
    throw new Error(`Swap tile ${params.tileId} not found or has no trade capture data`);
  }

  const typeIndicationFields = (() => {
    if (!isSwapRegularParams(params) || isNotDefined(params.properties.type)) {
      // you don't change the type : TC don't need to know about it
      return {}; // no type indication will be sent to TC
    }

    // the type properties is defined, so
    // - either we are loading the product from the workspace at startup
    // - either the user just changed it in the tile
    // in any case we need to inform TC about either isUneven either swapOffMarket

    const type = params.properties.type;
    switch (type) {
      case 'standard':
        return { isUneven: false };
      case 'uneven':
        return { isUneven: true };
      case 'historical':
        return { swapOffMarket: true };
      default:
        return assertUnreachable(type, `unknown type ${type}`);
    }
  })();

  return [
    {
      sessionId: swap.tradeCapture.sessionId,
      tileId: params.tileId,
      request: {
        idVersion: swap.tradeCapture.version + 1,

        changedFields: {
          currencyPair: params.properties.currencyPair ?? undefined,
          ...(params.isNonDeliverableChanged === true ? { isNonDeliverable: params.isNonDeliverable } : {}),

          legs: {
            0: {
              productName: 'FxSwap',
              currencyPair: params.properties.currencyPair ?? undefined,
              ...typeIndicationFields,

              // === NEAR DATA ===

              nearAmount1String:
                params.properties.nearNotionalCurrencyIndex === 1
                  ? isDefined(params.properties.nearNotional)
                    ? String(params.properties.nearNotional)
                    : ''
                  : undefined,

              nearAmount2String:
                params.properties.nearNotionalCurrencyIndex === 2
                  ? isDefined(params.properties.nearNotional)
                    ? String(params.properties.nearNotional)
                    : ''
                  : undefined,

              nearPaymentDateString: params.properties.nearExpiryDate ?? params.properties.nearExpiryDateTenor,

              nearPriceReferenceBidString:
                params.properties.nearPrice === null
                  ? null
                  : isDefined(params.properties.nearPrice?.bid)
                    ? String(params.properties.nearPrice.bid)
                    : undefined,

              nearPriceReferenceAskString:
                params.properties.nearPrice === null
                  ? null
                  : isDefined(params.properties.nearPrice?.ask)
                    ? String(params.properties.nearPrice.ask)
                    : undefined,

              // === FAR DATA ===

              farAmount1String:
                params.properties.farNotionalCurrencyIndex === 1
                  ? isDefined(params.properties.farNotional)
                    ? String(params.properties.farNotional)
                    : ''
                  : undefined,

              farAmount2String:
                params.properties.farNotionalCurrencyIndex === 2
                  ? isDefined(params.properties.farNotional)
                    ? String(params.properties.farNotional)
                    : ''
                  : undefined,

              farPaymentDateString: params.properties.farExpiryDate ?? params.properties.farExpiryDateTenor,

              farPriceReferenceBidString: !params.isNonDeliverable
                ? params.properties.farPrice === null
                  ? null
                  : isNotDefined(params.properties.farPrice?.bid)
                    ? params.properties.farPrice?.bid // null or undefined
                    : String(params.properties.farPrice.bid)
                : undefined,

              farPriceReferenceAskString: !params.isNonDeliverable
                ? params.properties.farPrice === null
                  ? null
                  : isDefined(params.properties.farPrice?.ask)
                    ? String(params.properties.farPrice.ask)
                    : undefined
                : undefined,

              xCurrency: params.isNonDeliverable ? params.properties.xCurrency : undefined,
              fixingCurrency: params.isNonDeliverable ? params.properties.fixingCurrency : undefined,
              fixingSource: params.isNonDeliverable ? params.properties.fixingSource : undefined,
              sndFixingSource: params.isNonDeliverable ? params.properties.sndFixingSource : undefined,
            },
          },
        },
      },
    },
  ];
};

export const isSwapRegularParams = (params: CheckSwapParams): params is CheckSwapRegularParams =>
  !params.isNonDeliverable;

// ██████╗ ███████╗███████╗██████╗  ██████╗ ███╗   ██╗███████╗███████╗
// ██╔══██╗██╔════╝██╔════╝██╔══██╗██╔═══██╗████╗  ██║██╔════╝██╔════╝
// ██████╔╝█████╗  ███████╗██████╔╝██║   ██║██╔██╗ ██║███████╗█████╗
// ██╔══██╗██╔══╝  ╚════██║██╔═══╝ ██║   ██║██║╚██╗██║╚════██║██╔══╝
// ██║  ██║███████╗███████║██║     ╚██████╔╝██║ ╚████║███████║███████╗
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚══════╝

// TRADE CAPTURE RESPONSE

export type TradeCaptureSwapResponse = Record<string, CheckedSwapResponse>;

export type CheckedSwapResponse = {
  idVersion: number;
  changedFields: {
    currencyPair?: CurrencyPair;
    currency1?: Currency;
    currency2?: Currency;

    isPriceObsolete?: boolean;
    isProductObsolete?: boolean;
    isReadyToPrice?: boolean;

    isNonDeliverable: boolean;

    legs: Record<string, CheckedSwapLegResponse>;
  };
  errors: TradeCaptureResponseErrors | null;
  warnings: TradeCaptureResponseWarnings | null;
};

type CheckedSwapLegResponse = {
  currencyPair: CurrencyPair;
  currency1: Currency;
  currency2: Currency;
  negotiatedCurrency: CurrencyIndex;

  productName: 'FxSwap';

  isReadyToPrice: boolean;
  isPriceObsolete: boolean;

  isUneven: boolean;

  nearAmount1: number | null;
  nearAmount1String: string | null;
  nearAmount2: number | null;
  nearAmount2String: string | null;
  nearPaymentDate: ISODate;
  nearPaymentDateTenor: Tenor;
  nearFixingDate: ISODate;
  nearPriceReferenceBid: Rate | null;
  nearPriceReferenceBidString: string | null;
  nearPriceReferenceAsk: Rate | null;
  nearPriceReferenceAskString: string | null;

  farAmount1: number | null;
  farAmount2: number | null;
  farPaymentDate: ISODate;
  farPaymentDateTenor: Tenor;
  farFixingDate: ISODate;
  farPriceReferenceBid: Rate | null;
  farPriceReferenceBidString: string | null;
  farPriceReferenceAsk: Rate | null;
  farPriceReferenceAskString: string | null;

  swapOffMarket: boolean;

  isCrossed: boolean;

  possibleXCurrencies: Possible<Currency>;
  xCurrency: Currency;

  possibleFixingSources: Possible<FixingSource>;
  fixingSource: FixingSource;
  fixingCurrency: Currency;

  possibleSndFixingSources: Possible<FixingSource>;
  sndFixingSource: FixingSource;
};

export type CheckedSwap = CheckedSwapResponse;
